import React from 'react';
import { Container, LinearProgress, Typography } from '@mui/material';
import type { ContainerProps } from '@mui/material';

export interface ProgressBarProps {
  current: number; // 1-based current step
  total: number; // total steps
  label?: string; // optional override label; defaults to `Step X of X`
  height?: number; // progress track height (px); default 8
  radius?: number; // border radius (theme radius units); default 2
  color?: string; // progress bar color; default 'primary.main'
  trackColor?: string; // track background color; default 'action.hover'
  containerProps?: ContainerProps; // to control width/gutters/margins
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  total,
  label,
  height = 8,
  radius = 2,
  color = 'primary.main',
  trackColor = 'action.hover',
  containerProps,
}) => {
  const safeTotal = Math.max(0, total);
  const safeCurrent = Math.min(Math.max(1, current), Math.max(1, safeTotal));
  const value = safeTotal > 0 ? (safeCurrent / safeTotal) * 100 : 0;

  const displayLabel =
    label ?? `Step ${safeTotal === 0 ? 0 : safeCurrent} of ${safeTotal}`;

  return (
    <Container maxWidth="md" disableGutters {...containerProps}>
      <Typography
        variant="body2"
        sx={{ fontSize: '1rem', color: 'primary.main', mb: 0.5 }}
      >
        {displayLabel}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height,
          borderRadius: radius,
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: radius,
          },
          bgcolor: trackColor,
        }}
      />
    </Container>
  );
};
